import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {AxiosError} from 'axios';
import {SagaIterator} from 'redux-saga';
import {all, call, fork, getContext, put, putResolve, select, takeEvery} from 'redux-saga/effects';

import {LOCAL_STORAGE_CHECKOUT} from 'constants/analytics';
import {CART_BUTTON} from 'constants/analytics/events/cart';
import {SEGMENT_FOR_MENTION_ME} from 'constants/analytics/mentionMe';
import {saveOrderDetails} from 'desktop/pages/checkout/store/sagas/analytics/analytics';
import {getAffId} from 'libs/analytics/affiliate';
import {apiConfig} from 'libs/api/api';
import {logWithSentry} from 'libs/sentry/sentry';

import {localStore} from 'libs/storage/storage';
import {ApplePayPaymentInfoType} from 'types/applePayType';
import {ActionType} from 'types/reducers';

import {
    completeOrderReviewApplePayExpress,
    saveApplePayExpress,
    saveApplePayExpressPayment,
    setLoadedApplePayExpress,
} from '../../../reducers/payments/applePayExpress/applePayExpress';
import {getApplePayOrder} from '../../../selectors/payments/payPalExpress/applePayExpress/applePayExpress';

/**
 * Redirect to success page if payment request is success
 */
function* redirectToSuccessPage(): SagaIterator {
    try {
        const orderRequest = yield select(getApplePayOrder);
        const navigate = yield getContext('navigate');
        if (orderRequest.success) {
            yield call(navigate, '/checkout/onepage/success/');
        } else if (orderRequest.error && orderRequest.redirect) {
            yield call(navigate, orderRequest.redirect);
        }
    } catch (error) {
        yield call([console, 'log'], error);
    }
}

/**
 * saveApplePayExpressPayment
 */
export function* placeOrderApplePayExpress(): SagaIterator {
    const {search} = apiConfig.hostStore?.getState()?.router?.location;
    const [affid] = yield all([call(getAffId, {search})]);

    const data = {affid, method: 'stripe_apple_pay'};

    const completeOrder = yield putResolve(completeOrderReviewApplePayExpress(data));
    yield call(
        [localStore, 'set'],
        SEGMENT_FOR_MENTION_ME,
        completeOrder?.action?.payload?.data?.mention_me_segment || '',
    );
    yield put(setLoadedApplePayExpress(false));

    yield call(redirectToSuccessPage);
}

/**
 * saveApplePayPayment
 */
export function* saveApplePay({payload}: ActionType<ApplePayPaymentInfoType>): SagaIterator {
    try {
        yield putResolve(saveApplePayExpressPayment({...payload}));

        yield put(setLoadedApplePayExpress(true));

        yield fork(placeOrderApplePayExpress);
    } catch (e) {
        yield put(setLoadedApplePayExpress(false));
    }
}

/**
 * prepareApplePayData
 */
export function* prepareApplePayData(): SagaIterator {
    yield call(saveOrderDetails);
    const data = yield call([localStore, 'get'], LOCAL_STORAGE_CHECKOUT);

    yield call(
        [localStore, 'set'],
        LOCAL_STORAGE_CHECKOUT,
        JSON.stringify({
            ...JSON.parse(data),
            paymentMethod: 'stripe_apple_pay',
        }),
    );
}

/**
 * sends analytics with error
 */
export function* sendErrorAnalytics({
    payload,
}: ActionType<AxiosError<{errors?: [{title: string}]}>>): SagaIterator {
    const error =
        payload.response?.data?.errors?.[0]?.title || 'Could not place order for Apple Pay Express';
    yield call(sendDesktopAnalyticsEvent, CART_BUTTON, {
        action: 'Express Checkout - ApplePay',
        label: `Error - ${error}`,
    });
    yield call(logWithSentry, new Error(error), payload);
}

/**
 * Saga for applePayExpress
 * */
export function* applePayExpressPaymentSaga(): SagaIterator {
    yield takeEvery(`${saveApplePayExpress}`, saveApplePay);
    yield takeEvery(`${completeOrderReviewApplePayExpress.fulfilled}`, prepareApplePayData);
    yield takeEvery(`${completeOrderReviewApplePayExpress.fulfilled}`, sendErrorAnalytics);
}
